import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { WidgetCard } from '../components/WidgetCard';
import { toast } from 'sonner';
import { 
  Folder, Users, MessageSquare, Activity, Send, Clock, 
  CheckCircle, AlertCircle, Loader, User, Mail, Phone, Briefcase,
  Edit, Trash2, X, Save, ArrowLeft, FileText, Upload, Download, 
  File, History, Plus, Calendar as CalendarIcon, Target, CalendarDays, Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import Calendar from '../components/Calendar';
import { SubForYou } from '../components/SubForYou';
import { SubPhotoGallery } from '../components/SubPhotoGallery';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const SubContractorDashboard = ({ user }) => {
  const navigate = useNavigate();
  const { userId } = useParams(); // Get userId from URL params for admin viewing
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [discussions, setDiscussions] = useState([]);
  const [statusUpdates, setStatusUpdates] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [activeTab, setActiveTab] = useState('overview'); // overview, discussions, team, activity, documents
  const [editingDiscussionId, setEditingDiscussionId] = useState(null);
  const [editingDiscussionContent, setEditingDiscussionContent] = useState('');
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [editingStatus, setEditingStatus] = useState('');
  const [editingStatusNotes, setEditingStatusNotes] = useState('');
  const [viewedUser, setViewedUser] = useState(null); // Store info about the user being viewed
  
  // Document management state
  const [documents, setDocuments] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('Contracts');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  // Calendar state
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [milestoneForm, setMilestoneForm] = useState({
    title: '',
    description: '',
    milestone_date: '',
    milestone_type: 'milestone'
  });
  const [taskForm, setTaskForm] = useState({
    assigned_to: '',
    title: '',
    description: '',
    scheduled_date: '',
    status: 'pending'
  });
  const [availabilityForm, setAvailabilityForm] = useState({
    date: '',
    available: true,
    notes: ''
  });

  const isAdminViewing = (user.role === 'admin' || user.role === 'supervisor') && userId && userId !== user.id;

  useEffect(() => {
    // Allow access for sub_contractors or admins
    if (user.role !== 'sub_contractor' && user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    
    // If admin is viewing someone else's dashboard, fetch that user's info
    if (isAdminViewing) {
      fetchViewedUserInfo();
    }
    
    fetchProjects();
  }, [user.role, navigate, userId]);

  useEffect(() => {
    if (selectedProject) {
      fetchProjectDetails(selectedProject.id);
      if (user.role === 'admin' || user.role === 'supervisor') {
        fetchDocuments(selectedProject.id);
      }
    }
  }, [selectedProject]);

  const fetchViewedUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}`);
      setViewedUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      toast.error('Failed to load user information');
    }
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects`);
      // Determine which user ID to filter by
      const targetUserId = isAdminViewing ? userId : user.id;

      // Own view: backend already returns only projects this sub-contractor can access
      // (team member, granted view permission, or creator). Admin-viewing: filter to that user's access.
      const myProjects = isAdminViewing
        ? response.data.filter(project =>
            (project.team_members || []).includes(targetUserId) ||
            (project.can_view_project || []).includes(targetUserId) ||
            (project.assigned_users || []).includes(targetUserId)
          )
        : response.data;
      setProjects(myProjects);
      if (myProjects.length > 0 && !selectedProject) {
        setSelectedProject(myProjects[0]);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectDetails = async (projectId) => {
    try {
      const discussionsRes = await axios.get(`${API}/projects/${projectId}/discussions`);
      setDiscussions(discussionsRes.data);
    } catch (error) {
      setDiscussions([]);
    }
    try {
      const statusRes = await axios.get(`${API}/projects/${projectId}/status-updates`);
      setStatusUpdates(statusRes.data);
    } catch (error) {
      setStatusUpdates([]);
    }
    try {
      const teamRes = await axios.get(`${API}/projects/${projectId}/team`);
      setTeamMembers(teamRes.data);
    } catch (error) {
      setTeamMembers([]);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedProject) return;

    try {
      await axios.post(`${API}/projects/${selectedProject.id}/discussions`, {
        project_id: selectedProject.id,
        content: newMessage
      });
      setNewMessage('');
      toast.success('Message posted');
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error('Failed to post message');
      console.error(error);
    }
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    if (!newStatus || !selectedProject) return;

    try {
      await axios.post(`${API}/projects/${selectedProject.id}/status-updates`, {
        project_id: selectedProject.id,
        status: newStatus,
        notes: statusNotes
      });
      setNewStatus('');
      setStatusNotes('');
      toast.success('Status updated');
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    }
  };

  const handleDeleteDiscussion = async (discussionId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await axios.delete(`${API}/projects/${selectedProject.id}/discussions/${discussionId}`);
      toast.success('Message deleted');
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error('Failed to delete message');
      console.error(error);
    }
  };

  const handleUpdateDiscussion = async (discussionId) => {
    if (!editingDiscussionContent.trim()) return;

    try {
      await axios.put(`${API}/projects/${selectedProject.id}/discussions/${discussionId}`, {
        content: editingDiscussionContent
      });
      toast.success('Message updated');
      setEditingDiscussionId(null);
      setEditingDiscussionContent('');
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error('Failed to update message');
      console.error(error);
    }
  };

  const handleDeleteStatus = async (statusId) => {
    if (!window.confirm('Are you sure you want to delete this status update?')) return;

    try {
      await axios.delete(`${API}/projects/${selectedProject.id}/status-updates/${statusId}`);
      toast.success('Status update deleted');
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error('Failed to delete status');
      console.error(error);
    }
  };

  const handleUpdateStatus = async (statusId) => {
    if (!editingStatus) return;

    try {
      await axios.put(`${API}/projects/${selectedProject.id}/status-updates/${statusId}`, {
        status: editingStatus,
        notes: editingStatusNotes
      });
      toast.success('Status updated');
      setEditingStatusId(null);
      setEditingStatus('');
      setEditingStatusNotes('');
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    }
  };

  const canEditDelete = (itemUserId) => {
    return (user.role === 'admin' || user.role === 'supervisor') || user.id === itemUserId;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'on_site': return 'bg-blue-500/20 text-blue-400 border-blue-500';
      case 'completed': return 'bg-green-500/20 text-green-400 border-green-500';
      case 'delayed': return 'bg-red-500/20 text-red-400 border-red-500';
      case 'in_progress': return 'bg-orange-500/20 text-orange-400 border-orange-500';
      case 'waiting': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500';
    }
  };

  // Document Management Functions
  const fetchDocuments = async (projectId) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!uploadFile || !selectedProject) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('category', uploadCategory);
      formData.append('description', uploadDescription);

      await axios.post(`${API}/projects/${selectedProject.id}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadDescription('');
      fetchDocuments(selectedProject.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const response = await axios.get(
        `${API}/projects/${selectedProject.id}/documents/${documentId}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Document downloaded');
    } catch (error) {
      toast.error('Failed to download document');
      console.error(error);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document and all its versions?')) {
      return;
    }

    try {
      await axios.delete(`${API}/projects/${selectedProject.id}/documents/${documentId}`);
      toast.success('Document deleted');
      fetchDocuments(selectedProject.id);
    } catch (error) {
      toast.error('Failed to delete document');
      console.error(error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Contracts': return <FileText className="w-5 h-5" />;
      case 'Blueprints': return <File className="w-5 h-5" />;
      case 'Invoices': return <FileText className="w-5 h-5" />;
      case 'Photos': return <Folder className="w-5 h-5" />;
      default: return <File className="w-5 h-5" />;
    }
  };

  const filteredDocuments = selectedCategory === 'all'
    ? documents
    : documents.filter(doc => doc.category === selectedCategory);

  // Calendar Functions
  const fetchCalendarEvents = async () => {
    try {
      const response = await axios.get(`${API}/calendar`);
      setCalendarEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch calendar events:', error);
      toast.error('Failed to load calendar');
    }
  };

  useEffect(() => {
    if (selectedProject) {
      fetchCalendarEvents();
    }
  }, [selectedProject]);

  const handleCreateMilestone = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;

    try {
      await axios.post(`${API}/projects/${selectedProject.id}/milestones`, {
        ...milestoneForm,
        project_id: selectedProject.id
      });
      
      toast.success('Milestone created successfully');
      setMilestoneDialogOpen(false);
      setMilestoneForm({ title: '', description: '', milestone_date: '', milestone_type: 'milestone' });
      fetchCalendarEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create milestone');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;

    try {
      await axios.post(`${API}/tasks`, {
        ...taskForm,
        project_id: selectedProject.id
      });
      
      toast.success('Task created successfully');
      setTaskDialogOpen(false);
      setTaskForm({ assigned_to: '', title: '', description: '', scheduled_date: '', status: 'pending' });
      fetchCalendarEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleSetAvailability = async (e) => {
    e.preventDefault();

    try {
      await axios.post(`${API}/availability`, availabilityForm);
      
      toast.success('Availability set successfully');
      setAvailabilityDialogOpen(false);
      setAvailabilityForm({ date: '', available: true, notes: '' });
      fetchCalendarEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set availability');
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  const handleDateClick = (dateStr) => {
    // Prefill forms with selected date
    setMilestoneForm(prev => ({ ...prev, milestone_date: dateStr }));
    setTaskForm(prev => ({ ...prev, scheduled_date: dateStr }));
    setAvailabilityForm(prev => ({ ...prev, date: dateStr }));
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'on_site': return 'On Site';
      case 'completed': return 'Completed';
      case 'delayed': return 'Delayed';
      case 'in_progress': return 'In Progress';
      case 'waiting': return 'Waiting';
      default: return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'on_site': return <Clock className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'delayed': return <AlertCircle className="w-4 h-4" />;
      case 'in_progress': return <Loader className="w-4 h-4 animate-spin" />;
      case 'waiting': return <Clock className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pb-20 pt-20 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pb-20 pt-20 flex items-center justify-center">
        <div className="text-center">
          <Folder className="w-16 h-16 mx-auto mb-4 text-slate-600" />
          <h2 className="text-2xl font-bold text-white mb-2">No Projects Assigned</h2>
          <p className="text-slate-400">Contact your project manager to get assigned to projects.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pb-20 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {!isAdminViewing && <WidgetCard />}
        {/* Header */}
        <div className="mb-8">
          {isAdminViewing && (
            <div className="mb-4 p-4 bg-indigo-500/20 border border-indigo-500 rounded-lg flex items-center justify-between">
              <div>
                <p className="text-indigo-300 font-semibold text-lg">
                  👁️ Viewing {viewedUser?.name || 'Subcontractor'}'s Dashboard
                </p>
                <p className="text-indigo-400 text-sm mt-1">
                  As admin, you can edit and delete all posts and status updates
                </p>
              </div>
              <Button
                onClick={() => navigate('/team')}
                variant="outline"
                className="border-indigo-500 text-indigo-300 hover:bg-indigo-600 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Team
              </Button>
            </div>
          )}
          <h1 className="text-3xl font-bold text-white mb-2">
            {isAdminViewing ? `${viewedUser?.name || 'Subcontractor'}'s Projects` : 'Sub-Contractor Dashboard'}
          </h1>
          <p className="text-slate-400">
            {isAdminViewing 
              ? `Viewing projects and activity for ${viewedUser?.name || 'this subcontractor'}`
              : `Welcome back, ${user.name}`
            }
          </p>
        </div>

        {/* Project Selector */}
        <div className="glass-card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Folder className="w-6 h-6 text-orange-400" />
              <h2 className="text-xl font-bold text-white">Active Projects</h2>
              <span className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full text-sm font-semibold">
                {projects.length}
              </span>
            </div>
          </div>
          
          <Select value={selectedProject?.id} onValueChange={(id) => {
            const project = projects.find(p => p.id === id);
            setSelectedProject(project);
          }}>
            <SelectTrigger className="w-full bg-slate-800 border-slate-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {projects.map(project => (
                <SelectItem key={project.id} value={project.id}>
                  {project.name} - {project.status || 'Active'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedProject && (
          <>
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <Card className="glass-card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Team Members</p>
                    <p className="text-3xl font-bold text-white">{teamMembers.length}</p>
                  </div>
                  <Users className="w-10 h-10 text-blue-400" />
                </div>
              </Card>
              
              <Card className="glass-card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Discussions</p>
                    <p className="text-3xl font-bold text-white">{discussions.length}</p>
                  </div>
                  <MessageSquare className="w-10 h-10 text-green-400" />
                </div>
              </Card>
              
              <Card className="glass-card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Status Updates</p>
                    <p className="text-3xl font-bold text-white">{statusUpdates.length}</p>
                  </div>
                  <Activity className="w-10 h-10 text-orange-400" />
                </div>
              </Card>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto">
              <Button
                onClick={() => setActiveTab('overview')}
                variant={activeTab === 'overview' ? 'default' : 'outline'}
                className={activeTab === 'overview' ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white'}
              >
                <Activity className="w-4 h-4 mr-2" />
                Overview
              </Button>
              <Button
                onClick={() => setActiveTab('photos')}
                variant={activeTab === 'photos' ? 'default' : 'outline'}
                className={activeTab === 'photos' ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white'}
                data-testid="sub-photos-tab-btn"
              >
                <Camera className="w-4 h-4 mr-2" />
                Photos
              </Button>
              <Button
                onClick={() => setActiveTab('discussions')}
                variant={activeTab === 'discussions' ? 'default' : 'outline'}
                className={activeTab === 'discussions' ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white'}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Discussions
              </Button>
              <Button
                onClick={() => setActiveTab('team')}
                variant={activeTab === 'team' ? 'default' : 'outline'}
                className={activeTab === 'team' ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white'}
              >
                <Users className="w-4 h-4 mr-2" />
                Team
              </Button>
              <Button
                onClick={() => setActiveTab('activity')}
                variant={activeTab === 'activity' ? 'default' : 'outline'}
                className={activeTab === 'activity' ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white'}
              >
                <Activity className="w-4 h-4 mr-2" />
                Activity Feed
              </Button>
              {(user.role === 'admin' || user.role === 'supervisor') && (
                <Button
                  onClick={() => setActiveTab('documents')}
                  variant={activeTab === 'documents' ? 'default' : 'outline'}
                  className={activeTab === 'documents' ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white'}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Documents
                </Button>
              )}
              <Button
                onClick={() => setActiveTab('calendar')}
                variant={activeTab === 'calendar' ? 'default' : 'outline'}
                className={activeTab === 'calendar' ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white'}
              >
                <CalendarIcon className="w-4 h-4 mr-2" />
                Calendar
              </Button>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Targeted photos & notes for this sub-contractor */}
                <SubForYou projectId={selectedProject.id} subId={isAdminViewing ? userId : user.id} />

                {/* Project Info */}
                <div className="glass-card p-6">
                  <h3 className="text-xl font-bold text-white mb-4">Project: {selectedProject.name}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-slate-300">
                    <div>
                      <p className="text-slate-500 text-sm">Status</p>
                      <p className="font-semibold">{selectedProject.status || 'Active'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-sm">Category</p>
                      <p className="font-semibold">{selectedProject.category || 'N/A'}</p>
                    </div>
                    {selectedProject.description && (
                      <div className="md:col-span-2">
                        <p className="text-slate-500 text-sm">Description</p>
                        <p className="font-semibold">{selectedProject.description}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Update Form */}
                <div className="glass-card p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Update Your Status</h3>
                  <form onSubmit={handleStatusUpdate} className="space-y-4">
                    <Select value={newStatus} onValueChange={setNewStatus}>
                      <SelectTrigger className="w-full bg-slate-800 border-slate-700 text-white">
                        <SelectValue placeholder="Select status..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="on_site">On Site</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed Today</SelectItem>
                        <SelectItem value="delayed">Delayed</SelectItem>
                        <SelectItem value="waiting">Waiting on Others</SelectItem>
                      </SelectContent>
                    </Select>
                    <Textarea
                      value={statusNotes}
                      onChange={(e) => setStatusNotes(e.target.value)}
                      placeholder="Add notes (optional)..."
                      className="bg-slate-800 border-slate-700 text-white"
                      rows={3}
                    />
                    <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
                      <Send className="w-4 h-4 mr-2" />
                      Post Status Update
                    </Button>
                  </form>
                </div>

                {/* Recent Status Updates */}
                {statusUpdates.length > 0 && (
                  <div className="glass-card p-6">
                    <h3 className="text-lg font-bold text-white mb-4">Recent Status Updates</h3>
                    <div className="space-y-3">
                      {statusUpdates.slice(0, 5).map(update => (
                        <div key={update.id} className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                          <div className={`p-2 rounded-lg ${getStatusColor(update.status)}`}>
                            {getStatusIcon(update.status)}
                          </div>
                          <div className="flex-1">
                            {editingStatusId === update.id ? (
                              <div className="space-y-2">
                                <Select value={editingStatus} onValueChange={setEditingStatus}>
                                  <SelectTrigger className="w-full bg-slate-800 border-slate-700 text-white">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="on_site">On Site</SelectItem>
                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                    <SelectItem value="delayed">Delayed</SelectItem>
                                    <SelectItem value="waiting">Waiting</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Textarea
                                  value={editingStatusNotes}
                                  onChange={(e) => setEditingStatusNotes(e.target.value)}
                                  placeholder="Notes..."
                                  className="bg-slate-800 border-slate-700 text-white"
                                  rows={2}
                                />
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleUpdateStatus(update.id)}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <Save className="w-3 h-3 mr-1" />
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingStatusId(null);
                                      setEditingStatus('');
                                      setEditingStatusNotes('');
                                    }}
                                  >
                                    <X className="w-3 h-3 mr-1" />
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-white">{update.user_name}</span>
                                    <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(update.status)} border`}>
                                      {getStatusLabel(update.status)}
                                    </span>
                                  </div>
                                  {canEditDelete(update.user_id) && (
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setEditingStatusId(update.id);
                                          setEditingStatus(update.status);
                                          setEditingStatusNotes(update.notes || '');
                                        }}
                                        className="border-blue-600 text-blue-400 hover:bg-blue-600 hover:text-white"
                                      >
                                        <Edit className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleDeleteStatus(update.id)}
                                        className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                                {update.notes && <p className="text-slate-300 text-sm">{update.notes}</p>}
                                <p className="text-slate-500 text-xs mt-1">
                                  {new Date(update.created_at).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'photos' && (
              <SubPhotoGallery
                projectId={selectedProject.id}
                subId={isAdminViewing ? userId : user.id}
                user={user}
                subName={isAdminViewing ? viewedUser?.name : null}
              />
            )}

            {activeTab === 'discussions' && (
              <div className="space-y-6">
                {/* Message Form */}
                <div className="glass-card p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Post a Message</h3>
                  <form onSubmit={handleSendMessage} className="space-y-4">
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Share updates, ask questions, or communicate with the team..."
                      className="bg-slate-800 border-slate-700 text-white min-h-[100px]"
                    />
                    <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
                      <Send className="w-4 h-4 mr-2" />
                      Post Message
                    </Button>
                  </form>
                </div>

                {/* Discussion Thread */}
                <div className="glass-card p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Project Discussion</h3>
                  {discussions.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                      <p className="text-slate-400">No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {discussions.map(discussion => (
                        <div key={discussion.id} className="flex gap-4 p-4 bg-slate-800/50 rounded-lg">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-orange-500 text-white">
                              {discussion.user_name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-white">{discussion.user_name}</span>
                                <span className="text-xs px-2 py-0.5 bg-slate-700 text-slate-300 rounded">
                                  {discussion.user_role.replace('_', ' ')}
                                </span>
                              </div>
                              {canEditDelete(discussion.user_id) && editingDiscussionId !== discussion.id && (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingDiscussionId(discussion.id);
                                      setEditingDiscussionContent(discussion.content);
                                    }}
                                    className="border-blue-600 text-blue-400 hover:bg-blue-600 hover:text-white"
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDeleteDiscussion(discussion.id)}
                                    className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                            
                            {editingDiscussionId === discussion.id ? (
                              <div className="space-y-2 mt-2">
                                <Textarea
                                  value={editingDiscussionContent}
                                  onChange={(e) => setEditingDiscussionContent(e.target.value)}
                                  className="bg-slate-800 border-slate-700 text-white"
                                  rows={3}
                                />
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleUpdateDiscussion(discussion.id)}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <Save className="w-3 h-3 mr-1" />
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingDiscussionId(null);
                                      setEditingDiscussionContent('');
                                    }}
                                  >
                                    <X className="w-3 h-3 mr-1" />
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-slate-300 whitespace-pre-wrap">{discussion.content}</p>
                            )}
                            
                            <p className="text-slate-500 text-xs mt-2">
                              {new Date(discussion.created_at).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'team' && (
              <div className="glass-card p-6">
                <h3 className="text-lg font-bold text-white mb-6">Project Team</h3>
                {teamMembers.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                    <p className="text-slate-400">No team members found</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {teamMembers.map(member => (
                      <div key={member.id} className="flex gap-4 p-4 bg-slate-800/50 rounded-lg">
                        <Avatar className="w-12 h-12">
                          <AvatarFallback className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                            {member.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-white">{member.name}</span>
                            <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded border border-blue-500">
                              {member.role.replace('_', ' ')}
                            </span>
                          </div>
                          {member.specialty && (
                            <div className="flex items-center gap-2 text-sm text-slate-300 mb-1">
                              <Briefcase className="w-3 h-3" />
                              {member.specialty}
                            </div>
                          )}
                          {member.company && (
                            <div className="flex items-center gap-2 text-sm text-slate-300 mb-1">
                              <User className="w-3 h-3" />
                              {member.company}
                            </div>
                          )}
                          {member.email && (
                            <a href={`mailto:${member.email}`} className="flex items-center gap-2 text-sm text-blue-300 underline" data-testid={`team-email-${member.id}`}>
                              <Mail className="w-3 h-3" />
                              {member.email}
                            </a>
                          )}
                          {member.phone && (
                            <a href={`tel:${member.phone}`} className="flex items-center gap-2 text-sm text-blue-300 underline mt-1" data-testid={`team-phone-${member.id}`}>
                              <Phone className="w-3 h-3" />
                              {member.phone}
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="glass-card p-6">
                <h3 className="text-lg font-bold text-white mb-6">Activity Feed</h3>
                <div className="space-y-3">
                  {[...statusUpdates, ...discussions]
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .slice(0, 20)
                    .map((item, index) => {
                      const isStatus = 'status' in item;
                      return (
                        <div key={`${isStatus ? 'status' : 'discussion'}-${item.id}-${index}`} className="flex gap-3 p-3 bg-slate-800/50 rounded-lg">
                          {isStatus ? (
                            <div className={`p-2 rounded-lg ${getStatusColor(item.status)}`}>
                              {getStatusIcon(item.status)}
                            </div>
                          ) : (
                            <div className="p-2 rounded-lg bg-blue-500/20 text-blue-400">
                              <MessageSquare className="w-4 h-4" />
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-white">{item.user_name}</span>
                              {isStatus && (
                                <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(item.status)} border`}>
                                  {getStatusLabel(item.status)}
                                </span>
                              )}
                            </div>
                            <p className="text-slate-300 text-sm">
                              {isStatus ? (item.notes || `Updated status to ${getStatusLabel(item.status)}`) : item.content}
                            </p>
                            <p className="text-slate-500 text-xs mt-1">
                              {new Date(item.created_at).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  {statusUpdates.length === 0 && discussions.length === 0 && (
                    <div className="text-center py-12">
                      <Activity className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                      <p className="text-slate-400">No activity yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (user.role === 'admin' || user.role === 'supervisor') && (
              <div className="space-y-6">
                {/* Upload Section */}
                <div className="glass-card p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-white">Project Documents</h3>
                    <Button
                      onClick={() => setUploadDialogOpen(true)}
                      className="bg-gradient-to-r from-green-500 to-green-600"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Document
                    </Button>
                  </div>

                  {/* Category Filter */}
                  <div className="flex gap-2 mb-6 overflow-x-auto">
                    <Button
                      onClick={() => setSelectedCategory('all')}
                      variant={selectedCategory === 'all' ? 'default' : 'outline'}
                      size="sm"
                      className={selectedCategory === 'all' ? 'bg-orange-500' : ''}
                    >
                      All ({documents.length})
                    </Button>
                    {['Contracts', 'Blueprints', 'Invoices', 'Photos', 'Other'].map(cat => (
                      <Button
                        key={cat}
                        onClick={() => setSelectedCategory(cat)}
                        variant={selectedCategory === cat ? 'default' : 'outline'}
                        size="sm"
                        className={selectedCategory === cat ? 'bg-orange-500' : ''}
                      >
                        {cat} ({documents.filter(d => d.category === cat).length})
                      </Button>
                    ))}
                  </div>

                  {/* Documents List */}
                  <div className="space-y-3">
                    {filteredDocuments.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                        <p className="text-slate-400 text-lg">No documents yet</p>
                        <p className="text-slate-500 text-sm mt-2">Upload your first document to get started</p>
                      </div>
                    ) : (
                      filteredDocuments.map((doc) => (
                        <div key={doc.id} className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                          <div className="flex items-start gap-4">
                            <div className="p-3 bg-blue-500/20 rounded-lg text-blue-400">
                              {getCategoryIcon(doc.category)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="text-white font-semibold truncate">{doc.filename}</h4>
                                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full whitespace-nowrap">
                                  {doc.category}
                                </span>
                              </div>
                              {doc.description && (
                                <p className="text-slate-400 text-sm mb-2">{doc.description}</p>
                              )}
                              <div className="flex items-center gap-4 text-xs text-slate-500">
                                <span>Uploaded by {doc.uploaded_by_name}</span>
                                <span>{new Date(doc.uploaded_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}</span>
                                <span>{formatFileSize(doc.file_size)}</span>
                                <span className="flex items-center gap-1">
                                  <History className="w-3 h-3" />
                                  v{doc.current_version} ({doc.versions?.length || 1} versions)
                                </span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadDocument(doc.id, doc.filename)}
                                className="border-blue-500 text-blue-400 hover:bg-blue-600 hover:text-white"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteDocument(doc.id)}
                                className="border-red-500 text-red-400 hover:bg-red-600 hover:text-white"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Upload Dialog */}
                {uploadDialogOpen && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-900 rounded-lg p-6 max-w-md w-full border border-slate-700">
                      <h3 className="text-xl font-bold text-white mb-4">Upload Document</h3>
                      <form onSubmit={handleUploadDocument} className="space-y-4">
                        <div>
                          <Label htmlFor="category" className="text-slate-300 font-semibold">Category *</Label>
                          <Select value={uploadCategory} onValueChange={setUploadCategory}>
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Contracts">Contracts</SelectItem>
                              <SelectItem value="Blueprints">Blueprints</SelectItem>
                              <SelectItem value="Invoices">Invoices</SelectItem>
                              <SelectItem value="Photos">Photos</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="file" className="text-slate-300 font-semibold">File *</Label>
                          <input
                            id="file"
                            type="file"
                            onChange={(e) => setUploadFile(e.target.files[0])}
                            className="mt-1 w-full text-slate-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="description" className="text-slate-300 font-semibold">Description</Label>
                          <Textarea
                            id="description"
                            value={uploadDescription}
                            onChange={(e) => setUploadDescription(e.target.value)}
                            placeholder="Optional description or notes"
                            className="mt-1"
                            rows={3}
                          />
                        </div>
                        <div className="flex gap-2 pt-4">
                          <Button
                            type="submit"
                            disabled={uploading || !uploadFile}
                            className="flex-1 bg-gradient-to-r from-green-500 to-green-600"
                          >
                            {uploading ? (
                              <>
                                <Loader className="w-4 h-4 mr-2 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Upload
                              </>
                            )}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setUploadDialogOpen(false);
                              setUploadFile(null);
                              setUploadDescription('');
                            }}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Calendar Tab */}
            {activeTab === 'calendar' && (
              <div className="space-y-6">
                <div className="glass-card p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-white">Project Calendar</h3>
                    <div className="flex gap-2">
                      {user.role === 'sub_contractor' && (
                        <Button
                          onClick={() => setAvailabilityDialogOpen(true)}
                          className="bg-gradient-to-r from-green-500 to-green-600"
                          size="sm"
                        >
                          <CalendarDays className="w-4 h-4 mr-2" />
                          Set Availability
                        </Button>
                      )}
                      {(user.role === 'admin' || user.role === 'supervisor') && (
                        <>
                          <Button
                            onClick={() => setMilestoneDialogOpen(true)}
                            className="bg-gradient-to-r from-purple-500 to-purple-600"
                            size="sm"
                          >
                            <Target className="w-4 h-4 mr-2" />
                            Add Milestone
                          </Button>
                          <Button
                            onClick={() => setTaskDialogOpen(true)}
                            className="bg-gradient-to-r from-blue-500 to-blue-600"
                            size="sm"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Assign Task
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="flex gap-4 mb-6 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                      <span className="text-slate-300">Milestones</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span className="text-slate-300">Deadlines</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                      <span className="text-slate-300">Tasks</span>
                    </div>
                    {user.role === 'sub_contractor' && (
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <span className="text-slate-300">Available</span>
                      </div>
                    )}
                  </div>

                  {/* Calendar Component */}
                  <Calendar
                    events={calendarEvents}
                    onEventClick={handleEventClick}
                    onDateClick={handleDateClick}
                  />
                </div>

                {/* Event Details */}
                {selectedEvent && (
                  <div className="glass-card p-6">
                    <h4 className="text-lg font-bold text-white mb-4">Event Details</h4>
                    <div className="space-y-3">
                      <div>
                        <span className="text-slate-400 text-sm">Type:</span>
                        <p className="text-white capitalize">{selectedEvent.type}</p>
                      </div>
                      <div>
                        <span className="text-slate-400 text-sm">Title:</span>
                        <p className="text-white">{selectedEvent.title}</p>
                      </div>
                      {selectedEvent.description && (
                        <div>
                          <span className="text-slate-400 text-sm">Description:</span>
                          <p className="text-white">{selectedEvent.description}</p>
                        </div>
                      )}
                      <div>
                        <span className="text-slate-400 text-sm">Date:</span>
                        <p className="text-white">{new Date(selectedEvent.date).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}</p>
                      </div>
                      {selectedEvent.project_name && (
                        <div>
                          <span className="text-slate-400 text-sm">Project:</span>
                          <p className="text-white">{selectedEvent.project_name}</p>
                        </div>
                      )}
                      {selectedEvent.status && (
                        <div>
                          <span className="text-slate-400 text-sm">Status:</span>
                          <p className="text-white capitalize">{selectedEvent.status}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Milestone Dialog */}
                {milestoneDialogOpen && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-900 rounded-lg p-6 max-w-md w-full border border-slate-700">
                      <h3 className="text-xl font-bold text-white mb-4">Add Milestone</h3>
                      <form onSubmit={handleCreateMilestone} className="space-y-4">
                        <div>
                          <Label className="text-slate-300">Type *</Label>
                          <Select
                            value={milestoneForm.milestone_type}
                            onValueChange={(val) => setMilestoneForm({ ...milestoneForm, milestone_type: val })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="milestone">Milestone</SelectItem>
                              <SelectItem value="deadline">Deadline</SelectItem>
                              <SelectItem value="future_project">Future Project</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-slate-300">Title *</Label>
                          <input
                            type="text"
                            value={milestoneForm.title}
                            onChange={(e) => setMilestoneForm({ ...milestoneForm, title: e.target.value })}
                            className="mt-1 w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Date *</Label>
                          <input
                            type="date"
                            value={milestoneForm.milestone_date}
                            onChange={(e) => setMilestoneForm({ ...milestoneForm, milestone_date: e.target.value })}
                            className="mt-1 w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Description</Label>
                          <Textarea
                            value={milestoneForm.description}
                            onChange={(e) => setMilestoneForm({ ...milestoneForm, description: e.target.value })}
                            className="mt-1"
                            rows={3}
                          />
                        </div>
                        <div className="flex gap-2 pt-4">
                          <Button type="submit" className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600">
                            Create Milestone
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setMilestoneDialogOpen(false)}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Task Dialog */}
                {taskDialogOpen && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-900 rounded-lg p-6 max-w-md w-full border border-slate-700">
                      <h3 className="text-xl font-bold text-white mb-4">Assign Task</h3>
                      <form onSubmit={handleCreateTask} className="space-y-4">
                        <div>
                          <Label className="text-slate-300">Assign To *</Label>
                          <Select
                            value={taskForm.assigned_to}
                            onValueChange={(val) => setTaskForm({ ...taskForm, assigned_to: val })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select team member" />
                            </SelectTrigger>
                            <SelectContent>
                              {teamMembers.map(member => (
                                <SelectItem key={member.id} value={member.id}>
                                  {member.name} ({member.role})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-slate-300">Title *</Label>
                          <input
                            type="text"
                            value={taskForm.title}
                            onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                            className="mt-1 w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Scheduled Date *</Label>
                          <input
                            type="date"
                            value={taskForm.scheduled_date}
                            onChange={(e) => setTaskForm({ ...taskForm, scheduled_date: e.target.value })}
                            className="mt-1 w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Description</Label>
                          <Textarea
                            value={taskForm.description}
                            onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                            className="mt-1"
                            rows={3}
                          />
                        </div>
                        <div className="flex gap-2 pt-4">
                          <Button type="submit" className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600">
                            Create Task
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setTaskDialogOpen(false)}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Availability Dialog */}
                {availabilityDialogOpen && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-900 rounded-lg p-6 max-w-md w-full border border-slate-700">
                      <h3 className="text-xl font-bold text-white mb-4">Set Availability</h3>
                      <form onSubmit={handleSetAvailability} className="space-y-4">
                        <div>
                          <Label className="text-slate-300">Date *</Label>
                          <input
                            type="date"
                            value={availabilityForm.date}
                            onChange={(e) => setAvailabilityForm({ ...availabilityForm, date: e.target.value })}
                            className="mt-1 w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Status *</Label>
                          <Select
                            value={availabilityForm.available.toString()}
                            onValueChange={(val) => setAvailabilityForm({ ...availabilityForm, available: val === 'true' })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="true">Available</SelectItem>
                              <SelectItem value="false">Not Available</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-slate-300">Notes</Label>
                          <Textarea
                            value={availabilityForm.notes}
                            onChange={(e) => setAvailabilityForm({ ...availabilityForm, notes: e.target.value })}
                            placeholder="Optional notes about your availability"
                            className="mt-1"
                            rows={3}
                          />
                        </div>
                        <div className="flex gap-2 pt-4">
                          <Button type="submit" className="flex-1 bg-gradient-to-r from-green-500 to-green-600">
                            Save Availability
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setAvailabilityDialogOpen(false)}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SubContractorDashboard;
